import Image from 'next/image';
import Link from 'next/link';
import {ReactNode, useEffect, useMemo, useState} from 'react';
import styled, {keyframes} from 'styled-components';
import PeopleSvg from '../../public/icons/people.svg';
import MessageSvg from '../../public/icons/message.svg';
import ProfileSvg from '../../public/icons/profile.svg';
import NewsSvg from '../../public/icons/news.svg';
import {SettingsButton} from './SettingsButton';
import {useRouter} from 'next/router';
import {CookieModal} from '@src/common/CookieModal';
import {useTranslation} from 'next-i18next';
import {NotificationsPopup} from '@src/features/notifications/NotificationsPopup';
import {devices} from '@src/common/screenSizes';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  useNotificationsNotInfiniteQuery,
  useUserAvatarQuery,
} from '@src/queries/user.queries';
import {useShowNotificationPopupOnNewVote} from '@src/features/notifications/useShowNotificationPopupOnNewVote';
import Modal from '@src/common/ui/Modal';
import {CustomButton} from '@src/common/inputs/CustomButton';
import {useQuery, useQueryClient} from 'react-query';
import {MessagesApi} from '@src/api/messages-api';
import {Message} from '@src/models/message';
import {getSocket} from '@src/utils/websocket';

dayjs.extend(utc);
dayjs.extend(timezone);

const HEADER_HEIGHT = '80px';

const MOBILE_MENU_BREAKPOINT = '750px';

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 10000;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px) saturate(180%);
  -webkit-backdrop-filter: blur(14px) saturate(180%);
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
  padding: 2rem;
  height: ${HEADER_HEIGHT};
  box-shadow: ${(p) => p.theme.elevation.sm};

  @media ${devices.sm} {
    padding: 1rem;
  }
`;

const HeaderNavList = styled.ul`
  display: flex;
  gap: 2rem;
  list-style: none;
  @media screen and (max-width: 500px) {
    gap: 10px;
  }
  @media screen and (max-width: 400px) {
    gap: 10px;
  }
`;
const HeaderNavListItem = styled.li`
  display: flex;
`;
const HeaderNavListItemMobileHamburger = styled.li`
  display: flex;
  @media (min-width: ${MOBILE_MENU_BREAKPOINT}) {
    display: none;
  }
`;
const HeaderNavListButton = styled.button`
  display: grid;
  place-items: center;
  position: relative;
  background-color: transparent;
  border: none;
  border-radius: 50%;
  padding: 6px;
  cursor: pointer;
  transition: background ${(p) => p.theme.motion.duration.fast} ease,
    transform ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.spring};

  &:hover {
    background: ${(p) => p.theme.colors.surfaceSunken};
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(0.94);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover,
    &:active {
      transform: none;
    }
  }
`;

const HeaderNavListButtonMobileHamburger = styled(HeaderNavListButton)``;

const MainContainer = styled.div`
  min-height: calc(100vh - ${HEADER_HEIGHT});
  display: grid;
  grid-template-columns: auto 1fr;
`;

const Aside = styled.aside<{$open: boolean}>`
  position: relative;
  z-index: 9999;
  width: 240px;
  height: 100%;
  background: ${(p) => p.theme.gradients.brand};
  background-attachment: fixed;

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 220px;
    background: radial-gradient(
      70% 60% at 30% 0%,
      rgba(255, 255, 255, 0.16) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    pointer-events: none;
  }

  @media (max-width: ${MOBILE_MENU_BREAKPOINT}) {
    position: absolute;
    left: 0;
    top: 0;
    transform: translateX(${(p) => (p.$open ? '0' : '-100%')});
    box-shadow: ${(p) => (p.$open ? p.theme.elevation.xl : 'none')};
    transition: transform ${(p) => p.theme.motion.duration.slow}
      ${(p) => p.theme.motion.easing.out};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ClosingIcon = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: none;
  cursor: pointer;
  @media (max-width: ${MOBILE_MENU_BREAKPOINT}) {
    display: block;
  }
`;

const PrimaryNavList = styled.ul`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 1.5rem 1rem;
  list-style: none;
  @media (max-width: ${MOBILE_MENU_BREAKPOINT}) {
  margin-top: 70px;
}
`;

const PrimaryNavItem = styled.li<{toHighlight: boolean}>`
  position: relative;
  list-style: none;
  display: block;
  font-weight: ${(p) => (p.toHighlight ? 600 : 500)};
  font-size: 14px;
  line-height: 21px;
  color: ${(props) =>
    props.toHighlight
      ? props.theme.colors.neutral100
      : 'rgba(255, 255, 255, 0.62)'};
  background-color: ${(p) =>
    p.toHighlight ? 'rgba(255, 255, 255, 0.14)' : 'transparent'};
  border-radius: 12px;
  padding: 11px 12px;
  transition: background ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out},
    color ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out};

  /* pionowy wskaznik aktywnej zakladki */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    width: 3px;
    height: ${(p) => (p.toHighlight ? '20px' : '0')};
    border-radius: 0 3px 3px 0;
    background: ${(p) => p.theme.colors.neutral100};
    transform: translateY(-50%);
    transition: height ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.spring};
  }

  &:hover {
    color: ${(props) => props.theme.colors.neutral100};
    background-color: rgba(255, 255, 255, 0.09);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::before {
      transition: none;
    }
  }
`;

const PrimaryNavLink = styled(Link)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  min-height: 24px;
  width: 100%;
`;

const NavIcon = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  line-height: 0;

  > svg {
    display: block;
    width: auto;
    height: 18px;
    max-width: 24px;
  }
`;

const NavLabel = styled.span`
  display: block;
  line-height: 1.2;
  padding-top: 1px;
`;

const Main = styled.main`
  padding: 1.5rem 1rem;
  min-width: 0;

  @media (max-width: ${MOBILE_MENU_BREAKPOINT}) {
    margin: 0 auto;
    width: 100vw;
    padding: 1rem;
  }
`;

const notificationPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(225, 1, 1, 0.45); }
  70% { box-shadow: 0 0 0 6px rgba(225, 1, 1, 0); }
  100% { box-shadow: 0 0 0 0 rgba(225, 1, 1, 0); }
`;

const NotificationDot = styled.span`
  position: absolute;
  top: 0px;
  right: 0px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(p) => p.theme.colors.danger500};
  border: 2px solid ${(p) => p.theme.colors.neutral100};
  animation: ${notificationPulse} 2s ${(p) => p.theme.motion.easing.standard}
    infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

interface UserLayoutProps {
  children: ReactNode;
}

export const UserLayout = ({children}: UserLayoutProps) => {
  const router = useRouter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotificationsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const {t} = useTranslation();

  const PrimaryNavigationItems = [
    {
      href: '/messages/chats',
      Icon: MessageSvg,
      label: t('pages:chat'),
    },
    {
      href: '/memb-community',
      Icon: PeopleSvg,
      label: t('pages:tab-community'),
    },
    {
      href: '/profile',
      Icon: ProfileSvg,
      label: t('pages:my-profile'),
    },
    {
      href: '/memb-community?topicType=NEWS',
      Icon: NewsSvg,
      label: t('pages:tab-posts'),
    },
  ];

  const GuestNavigationItems = [
    {href: '/sign-in', Icon: ProfileSvg, label: t('common:login')},
    {
      href: '/choose-registration',
      Icon: ProfileSvg,
      label: t('common:register'),
    },
  ];

  const {data: avatar} = useUserAvatarQuery();

  const [token, setToken] = useState('');

  useEffect(() => {
    const tmpToken = localStorage.getItem('jwt');
    if (tmpToken) {
      setToken(tmpToken);
    }
  }, [token]);

  const {data} = useNotificationsNotInfiniteQuery(token);

  const queryClient = useQueryClient();
  const {data: unseenChatsData} = useQuery(
    ['messages-unseen-count'],
    () =>
      MessagesApi.getMessages({page: 1, sort_by: 'lastMessageDate_DESC'}),
    {enabled: !!token}
  );
  const hasUnseenChats = (unseenChatsData?.unseenChatsCount ?? 0) > 0;

  useEffect(() => {
    if (!token) {
      return;
    }
    const socket = getSocket(token);
    function onMessage(message: Message) {
      if (message.sendByMe || router.query.id === message.otherSideUserId) {
        return;
      }
      queryClient.invalidateQueries('messages-unseen-count');
      queryClient.invalidateQueries('messagesList');
    }
    socket.on('message', onMessage);
    socket.on('messageWithAttachment', onMessage);
    return () => {
      socket.off('message', onMessage);
      socket.off('messageWithAttachment', onMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const [isNewVoteModalOpen, setIsNewVoteModalOpen] = useState(false);
  const [toCheckVoteId, setToCheckVoteId] = useState<string | undefined>(
    undefined
  );
  useShowNotificationPopupOnNewVote(setIsNewVoteModalOpen, setToCheckVoteId);

  const newNotifications = useMemo(() => {
    return data?.data?.filter((el) => el.status === 'UNREAD');
  }, [data?.data]);

  const toHighlightTab = useMemo(() => {
    if (router.route.includes('profile')) {
      return '/profile';
    }
    if (router.route.includes('messages')) {
      return '/messages/chats';
    }
    if (router.route.includes('memb-community')) {
      if (router.query.topicType === 'NEWS') {
        return '/memb-community?topicType=NEWS';
      }
      return '/memb-community';
    }

    return null;
  }, [router.route, router.query]);

  const handleAvatarClick = () => {
    router.push('/profile');
  };

  return (
    <div>
      <Header>
        <Link href="/">
          <Image
            alt="Membplace logo"
            src={'/icons/logo.svg'}
            width={90}
            height={60}
          />
        </Link>
        <nav>
          {token && (
            <HeaderNavList>
              <HeaderNavListItem>
                <HeaderNavListButton>
                  <SettingsButton
                    isOpen={isSettingsOpen}
                    setIsOpen={() => {
                      setNotificationsOpen(false);
                      setIsSettingsOpen((prev) => !prev);
                    }}
                  />
                </HeaderNavListButton>
              </HeaderNavListItem>
              <HeaderNavListItem>
                <HeaderNavListButton
                  onClick={() => {
                    setNotificationsOpen((prev) => !prev);
                    setIsSettingsOpen(false);
                  }}
                >
                  <Image
                    alt="Notifications"
                    src={'/icons/bell.svg'}
                    width={24}
                    height={24}
                  />
                  {newNotifications && newNotifications.length > 0 && (
                    <NotificationDot />
                  )}
                </HeaderNavListButton>
              </HeaderNavListItem>
              {notifications && (
                <NotificationsPopup setOpen={setNotificationsOpen} />
              )}
              <HeaderNavListItem>
                <HeaderNavListButton
                  onClick={handleAvatarClick}
                  style={{width: 44, padding: 0}}
                >
                  {avatar ? (
                    <Image
                      alt="Avatar"
                      src={avatar}
                      width={44}
                      height={44}
                      style={{
                        objectFit: 'cover',
                        borderRadius: '999px',
                      }}
                    />
                  ) : (
                    <Image
                      alt="Avatar"
                      src={'/icons/avatar.svg'}
                      width={44}
                      height={44}
                    />
                  )}
                </HeaderNavListButton>
              </HeaderNavListItem>
              <HeaderNavListItemMobileHamburger>
                <HeaderNavListButtonMobileHamburger
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  <Image
                    alt="Avatar"
                    src={'/icons/hamburger.svg'}
                    width={24}
                    height={24}
                  />
                </HeaderNavListButtonMobileHamburger>
              </HeaderNavListItemMobileHamburger>
            </HeaderNavList>
          )}
        </nav>
      </Header>
      <MainContainer>
        <Aside $open={mobileMenuOpen}>
          <ClosingIcon onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9Z"
                fill="#2F3071"
              />
              <path
                d="M8.96371 0C7.19086 0 5.45782 0.525712 3.98374 1.51066C2.50967 2.4956 1.36077 3.89554 0.682326 5.53345C0.00388384 7.17135 -0.173628 8.97365 0.172239 10.7124C0.518106 12.4512 1.37182 14.0484 2.62541 15.302C3.87901 16.5556 5.47619 17.4093 7.21498 17.7552C8.95377 18.101 10.7561 17.9235 12.394 17.2451C14.0319 16.5667 15.4318 15.4178 16.4168 13.9437C17.4017 12.4696 17.9274 10.7366 17.9274 8.96371C17.9249 6.58716 16.9797 4.30866 15.2992 2.62818C13.6188 0.947704 11.3403 0.00250968 8.96371 0ZM12.2096 11.2339C12.2737 11.298 12.3245 11.3741 12.3592 11.4578C12.3938 11.5415 12.4117 11.6312 12.4117 11.7218C12.4117 11.8124 12.3938 11.9021 12.3592 11.9858C12.3245 12.0695 12.2737 12.1455 12.2096 12.2096C12.1455 12.2737 12.0695 12.3245 11.9858 12.3592C11.9021 12.3938 11.8124 12.4117 11.7218 12.4117C11.6312 12.4117 11.5415 12.3938 11.4578 12.3592C11.3741 12.3245 11.298 12.2737 11.2339 12.2096L8.96371 9.93851L6.69348 12.2096C6.62942 12.2737 6.55336 12.3245 6.46966 12.3592C6.38596 12.3938 6.29625 12.4117 6.20565 12.4117C6.11505 12.4117 6.02534 12.3938 5.94164 12.3592C5.85793 12.3245 5.78188 12.2737 5.71782 12.2096C5.65375 12.1455 5.60294 12.0695 5.56826 11.9858C5.53359 11.9021 5.51575 11.8124 5.51575 11.7218C5.51575 11.6312 5.53359 11.5415 5.56826 11.4578C5.60294 11.3741 5.65375 11.298 5.71782 11.2339L7.98891 8.96371L5.71782 6.69348C5.58843 6.5641 5.51575 6.38862 5.51575 6.20565C5.51575 6.02267 5.58843 5.84719 5.71782 5.71781C5.8472 5.58843 6.02268 5.51575 6.20565 5.51575C6.38862 5.51575 6.5641 5.58843 6.69348 5.71781L8.96371 7.98891L11.2339 5.71781C11.298 5.65375 11.3741 5.60293 11.4578 5.56826C11.5415 5.53359 11.6312 5.51575 11.7218 5.51575C11.8124 5.51575 11.9021 5.53359 11.9858 5.56826C12.0695 5.60293 12.1455 5.65375 12.2096 5.71781C12.2737 5.78188 12.3245 5.85793 12.3592 5.94163C12.3938 6.02533 12.4117 6.11505 12.4117 6.20565C12.4117 6.29624 12.3938 6.38596 12.3592 6.46966C12.3245 6.55336 12.2737 6.62941 12.2096 6.69348L9.93852 8.96371L12.2096 11.2339Z"
                fill="white"
              />
            </svg>
          </ClosingIcon>
          <nav>
            {token && (
              <PrimaryNavList>
                {PrimaryNavigationItems.map((item) => (
                  <PrimaryNavItem
                    key={item.href}
                    toHighlight={item.href === toHighlightTab}
                  >
                    <PrimaryNavLink href={item.href}>
                      <NavIcon>
                        <item.Icon />
                        {item.href === '/messages/chats' && hasUnseenChats && (
                          <NotificationDot />
                        )}
                      </NavIcon>
                      <NavLabel>{item.label}</NavLabel>
                    </PrimaryNavLink>
                  </PrimaryNavItem>
                ))}
              </PrimaryNavList>
            )}
            {!token && (
              <PrimaryNavList>
                {GuestNavigationItems.map((item) => (
                  <PrimaryNavItem
                    key={item.href}
                    toHighlight={item.href === toHighlightTab}
                  >
                    <PrimaryNavLink href={item.href}>
                      <NavIcon>
                        <item.Icon />
                      </NavIcon>
                      <NavLabel>{item.label}</NavLabel>
                    </PrimaryNavLink>
                  </PrimaryNavItem>
                ))}
              </PrimaryNavList>
            )}
          </nav>
        </Aside>

        <Main>
          {children} <CookieModal />
        </Main>

        <Modal
          title={t('common:new-vote-in-your-event')}
          show={isNewVoteModalOpen}
          onClose={() => setIsNewVoteModalOpen(false)}
          width="500px"
          height="350px"
        >
          <Text>{t('common:check-your-notifications')}</Text>
          <CustomButton
            style={{
              marginTop: 50,
            }}
            onClick={() => {
              router.push('/voting/voting-info/' + toCheckVoteId);
            }}
            type="button"
            label={t('common:view-details')}
            buttonType={'secondary'}
          ></CustomButton>
        </Modal>
      </MainContainer>
    </div>
  );
};

const Text = styled.p`
  margin-top: 20px;
  text-align: center;
`;
